import { Link, useLocation } from "react-router-dom";
import {
  Plus,
  LayoutDashboard,
  Folder,
  PackageSearch,
  Users,
  X,
  UserRound,
  LogOut,
  ChevronRight,
  ChevronDown,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { mockCurrentUser } from "@/lib/currentUser";
import { getAuthenticatedUser, clearAuthenticatedUser } from "@/lib/auth";

// ----------------------------------------------------------------------------

interface SidebarProps {
  className?: string;
  onNavigate?: () => void;
  onClose?: () => void;
}

export function Sidebar(props: SidebarProps) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const { className, onNavigate, onClose } = props;
  const activeUser = getAuthenticatedUser() ?? mockCurrentUser;

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const location = useLocation();

  // -------------------------------------
  // Effects
  // -------------------------------------

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function handleLogout() {
    clearAuthenticatedUser();
    onNavigate?.();
    window.location.href = "https://www.gealuce.com";
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  const currentPath = location.pathname;

  return (
    <aside
      className={`flex flex-col h-full w-64 bg-gray-50 border-r border-gray-200 ${className || ""}`}
    >
      {/* Logo */}
      <div className="flex items-center justify-between gap-3 p-6">
        <a
          href="https://www.gealuce.com"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onNavigate}
          className="flex items-center gap-3 min-w-0 hover:opacity-90 transition-opacity"
        >
          <div className="w-8 h-8 bg-[#E9E21B] rounded flex items-center justify-center font-bold text-lg">
            G
          </div>
          <span className="font-bold text-gray-900 text-lg truncate">
            Gea Luce
          </span>
        </a>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close menu"
            onClick={onClose}
            className="lg:hidden"
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Main Nav */}
      <div className="px-4 flex-1">
        <Link
          to="/nuovo-ordine"
          className="block w-full mb-6"
          onClick={onNavigate}
        >
          <Button className="w-full bg-[#0D6EFD] hover:bg-blue-700 text-white justify-start gap-2 h-10 px-4">
            <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center">
              <Plus className="w-3.5 h-3.5 text-white" />
            </div>
            Nuovo ordine
          </Button>
        </Link>

        <nav className="space-y-1">
          <Link
            to="/"
            onClick={onNavigate}
            className={`flex items-center justify-between px-3 py-2 rounded-md font-medium transition-colors ${
              currentPath === "/"
                ? "bg-[#E9E21B] text-black"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <div className="flex items-center gap-3">
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </div>
            <ChevronRight className="w-4 h-4" />
          </Link>

          <Link
            to="/ordini"
            onClick={onNavigate}
            className={`flex items-center justify-between px-3 py-2 rounded-md font-medium transition-colors ${
              currentPath === "/ordini"
                ? "bg-[#E9E21B] text-black"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <div className="flex items-center gap-3">
              <Folder className="w-5 h-5" />
              Ordini
            </div>
            <ChevronRight className="w-4 h-4" />
          </Link>

          <Link
            to="/clienti"
            onClick={onNavigate}
            className={`flex items-center justify-between px-3 py-2 rounded-md font-medium transition-colors ${
              currentPath === "/clienti"
                ? "bg-[#E9E21B] text-black"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5" />
              Clienti
            </div>
            <ChevronRight className="w-4 h-4" />
          </Link>

          <Link
            to="/prodotti"
            onClick={onNavigate}
            className={`flex items-center justify-between px-3 py-2 rounded-md font-medium transition-colors ${
              currentPath === "/prodotti"
                ? "bg-[#E9E21B] text-black"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <div className="flex items-center gap-3">
              <PackageSearch className="w-5 h-5" />
              Prodotti
            </div>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </nav>
      </div>

      {/* Bottom Nav & Profile */}
      <div className="p-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-3 px-3 py-2 text-left cursor-pointer hover:bg-gray-100 rounded-md transition-colors"
            >
              <Avatar className="w-10 h-10 rounded-sm">
                <AvatarFallback className="bg-[#E9E21B] text-black rounded-sm font-semibold">
                  {activeUser.initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 overflow-hidden">
                <p className="text-sm font-bold text-gray-900 truncate">
                  {activeUser.fullName}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {activeUser.email}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-64">
            <DropdownMenuLabel className="py-3">
              <div className="text-sm font-semibold text-gray-900">
                {activeUser.fullName}
              </div>
              <div className="text-xs font-normal text-gray-500 mt-1">
                {activeUser.email}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link
                to="/profilo"
                onClick={onNavigate}
                className="flex items-center gap-2 cursor-pointer w-full"
              >
                <UserRound className="w-4 h-4" />
                Profilo Agente
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
              Esci
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
